/**
 * Главный файл данных курса
 *
 * Этот файл предоставляет унифицированный доступ к данным курса,
 * импортируя модули из директории modules/.
 *
 * Модули разбиты на отдельные файлы для:
 * - Улучшения производительности сборки
 * - Упрощения навигации и поддержки
 * - Возможности будущего lazy loading
 */

import type { Course, CourseModule, Lesson } from '@/types/lesson';
import { allModules, getModuleBySlug as getModuleBySlugFromModules } from './modules';

/**
 * Курс Git
 */
export const course: Course = {
  id: 'git-course',
  title: 'Мастерство Git',
  description: 'Изучите Git от основ до продвинутых техник с интерактивными уроками',
  modules: allModules,
};

/**
 * Получить урок по ID
 */
export function getLessonById(lessonId: string): Lesson | undefined {
  for (const module of course.modules) {
    const lesson = module.lessons.find((l) => l.id === lessonId);
    if (lesson) return lesson;
  }
  return undefined;
}

/**
 * Получить урок по slug
 */
export function getLessonBySlug(moduleSlug: string, lessonSlug: string): Lesson | undefined {
  const module = course.modules.find((m) => m.slug === moduleSlug);
  if (!module) return undefined;
  return module.lessons.find((l) => l.slug === lessonSlug);
}

/**
 * Получить модуль по slug
 */
export function getModuleBySlug(slug: string): CourseModule | undefined {
  return getModuleBySlugFromModules(slug);
}

/**
 * Получить следующий урок
 */
export function getNextLesson(lessonId: string): Lesson | undefined {
  for (let i = 0; i < course.modules.length; i++) {
    const module = course.modules[i];
    const lessonIndex = module.lessons.findIndex((l) => l.id === lessonId);

    if (lessonIndex !== -1) {
      // Следующий урок в том же модуле
      if (lessonIndex < module.lessons.length - 1) {
        return module.lessons[lessonIndex + 1];
      }
      // Первый урок следующего модуля
      if (i < course.modules.length - 1) {
        return course.modules[i + 1].lessons[0];
      }
    }
  }
  return undefined;
}

/**
 * Получить предыдущий урок
 */
export function getPreviousLesson(lessonId: string): Lesson | undefined {
  for (let i = 0; i < course.modules.length; i++) {
    const module = course.modules[i];
    const lessonIndex = module.lessons.findIndex((l) => l.id === lessonId);

    if (lessonIndex !== -1) {
      // Предыдущий урок в том же модуле
      if (lessonIndex > 0) {
        return module.lessons[lessonIndex - 1];
      }
      // Последний урок предыдущего модуля
      if (i > 0) {
        const prevModule = course.modules[i - 1];
        return prevModule.lessons[prevModule.lessons.length - 1];
      }
    }
  }
  return undefined;
}

/**
 * Получить модуль и урок по ID урока
 */
export function getModuleAndLessonByLessonId(
  lessonId: string
): { module: CourseModule; lesson: Lesson } | undefined {
  for (const module of course.modules) {
    const lesson = module.lessons.find((l) => l.id === lessonId);
    if (lesson) {
      return { module, lesson };
    }
  }
  return undefined;
}
